import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { getMyMemberships } from 'API/auth'
import { useActivePlayProfile } from 'hooks/use-active-play-profile'
import Avatar from 'components/Avatar'
import { DARK_CARD_SELECTED_BORDER, DARK_SURFACE_GRADIENT, DARK_TABLE_BORDER } from 'constants/dark-tokens'
import { EMembershipStatus } from 'constants/membership'
import { useStores } from 'hooks/useStores'
import routes from 'routes'
import { MembershipInfo } from 'types/auth'

type SwitchTeamMode = 'dark' | 'light'

interface ISwitchTeamModalProps {
  isOpen: boolean
  onClose: () => void
  /** Visual surface. `dark` (default) keeps the original RallyPD look used on
   *  the Team Members page; `light` is for white-surface coach pages. */
  mode?: SwitchTeamMode
  /** Controlled active highlight. Defaults to the global `activeOrgId`. */
  selectedOrgId?: number | null
  /** Controlled select handler. When provided the modal does NOT mutate the
   *  global active org — selection stays owned by the caller (per-page scope). */
  onSelect?: (orgId: number) => void
  /** Adds the "Create new organization" row. Off by default so the per-page team
   *  scoping use (coach list pages) stays a pure switcher. */
  showCreateOrg?: boolean
}

interface SwitchTeamTokens {
  modalBg: string
  modalBorder: string
  headerBorder: string
  cardBg: string
  cardBorderActive: string
  cardHoverBorder: string
  text: string
  subText: string
  badgeBg: string
  closeColor: string
}

export const SWITCH_TEAM_TOKENS: Record<SwitchTeamMode, SwitchTeamTokens> = {
  dark: {
    modalBg: DARK_SURFACE_GRADIENT,
    modalBorder: '1px solid rgba(107,121,143,0.22)',
    headerBorder: DARK_TABLE_BORDER,
    cardBg: DARK_SURFACE_GRADIENT,
    cardBorderActive: DARK_CARD_SELECTED_BORDER,
    cardHoverBorder: 'rgba(255,255,255,0.12)',
    text: '#F4F8FB',
    subText: 'rgba(244,248,251,0.6)',
    badgeBg: 'rgba(255,255,255,0.12)',
    closeColor: 'rgba(244,248,251,0.7)'
  },
  light: {
    modalBg: '#FFFFFF',
    modalBorder: '1px solid #E2E8F0',
    headerBorder: '#E2E8F0',
    cardBg: '#F7FAFC',
    cardBorderActive: '#3182CE',
    cardHoverBorder: '#CBD5E0',
    text: '#1A202C',
    subText: '#718096',
    badgeBg: '#EDF2F7',
    closeColor: '#718096'
  }
}

export const TeamCard = ({
  membership,
  isActive,
  tokens,
  onSelect
}: {
  membership: MembershipInfo
  isActive: boolean
  tokens: SwitchTeamTokens
  onSelect: () => void
}) => (
  <Box
    as="button"
    w="100%"
    px={4}
    py={3}
    background={tokens.cardBg}
    border={`1px solid ${isActive ? tokens.cardBorderActive : 'transparent'}`}
    borderRadius="16px"
    cursor="pointer"
    textAlign="left"
    onClick={onSelect}
    _hover={{ borderColor: isActive ? tokens.cardBorderActive : tokens.cardHoverBorder }}
    _focus={{ boxShadow: 'none', outline: 'none' }}
    _focusVisible={{ boxShadow: 'none', outline: 'none' }}
  >
    <HStack spacing={3}>
      <Avatar src={membership.orgLogo ?? ''} name={membership.orgName} size={40} />
      <VStack align="flex-start" spacing={0} flex={1} minW={0}>
        <Text color={tokens.text} fontSize="15px" fontWeight={600} m={0} noOfLines={1}>
          {membership.orgName}
        </Text>
        {typeof membership.memberCount === 'number' && (
          <Text color={tokens.subText} fontSize="13px" m={0}>
            {membership.memberCount} members
          </Text>
        )}
      </VStack>
    </HStack>
  </Box>
)

/**
 * In-page team switcher modal. Lists the teams the user belongs to with
 * per-org member counts and highlights the active team.
 *
 * Two usage shapes:
 * - Uncontrolled (default): selecting soft-switches the GLOBAL active org via
 *   `authStore.setActiveOrg` (Team Members page).
 * - Controlled (`onSelect` provided): selection is reported to the caller and
 *   the global org is left untouched — used by coach list pages that scope a
 *   single team PER PAGE.
 */
const SwitchTeamModal = ({
  isOpen,
  onClose,
  mode = 'dark',
  selectedOrgId,
  onSelect,
  showCreateOrg = false
}: ISwitchTeamModalProps) => {
  const { authStore } = useStores()
  const navigate = useNavigate()
  const { active: activePlayProfile } = useActivePlayProfile()
  // Starting an organization is a coach's act. An athlete — a player or a youth
  // profile — is only ever a member of one, so the row is withheld here rather
  // than at each call site: the modal is shared, and a caller that simply asks
  // for the row cannot know who is looking.
  const isAthleteViewer = activePlayProfile?.role === 'player' || activePlayProfile?.role === 'child'
  const canCreateOrg = showCreateOrg && !isAthleteViewer
  const { activeOrgId } = authStore
  const tokens = SWITCH_TEAM_TOKENS[mode]
  const activeHighlightId = selectedOrgId ?? activeOrgId
  const [teams, setTeams] = useState<MembershipInfo[]>(authStore.memberships ?? [])

  useEffect(() => {
    if (!isOpen) return
    // Refetch on open so member counts are present (login bootstrap omits them).
    getMyMemberships()
      .then(data => setTeams(data ?? []))
      .catch(() => setTeams(authStore.memberships ?? []))
  }, [isOpen])

  // Only joined teams are switchable; pending invites are excluded.
  const joinedTeams = teams.filter(t => t.status !== EMembershipStatus.PENDING)

  const handleSelect = (orgId: number) => {
    if (onSelect) {
      // Controlled: caller owns the selection; never touch the global org.
      onSelect(orgId)
    } else if (orgId !== activeOrgId) {
      // Soft-switch: update the active org context; observers re-render and
      // team-scoped fetches re-run without a full page reload.
      authStore.setActiveOrg(orgId)
    }
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent background={tokens.modalBg} borderRadius="18px" border={tokens.modalBorder} mx={4}>
        <ModalHeader pb={3} borderBottom={`1px solid ${tokens.headerBorder}`}>
          <HStack spacing={2}>
            <Text color={tokens.text} fontSize="18px" fontWeight={700} m={0} textTransform="uppercase">
              Organization
            </Text>
            <Badge bg={tokens.badgeBg} color={tokens.text} borderRadius="full" px={2}>
              {joinedTeams.length}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton
          bg="transparent"
          border="none"
          boxShadow="none"
          color={tokens.closeColor}
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
          _focusVisible={{ boxShadow: 'none', outline: 'none' }}
        />
        <ModalBody pb={5} pt={4}>
          <VStack spacing={3} align="stretch">
            {joinedTeams.map(m => (
              <TeamCard
                key={m.orgId}
                membership={m}
                isActive={m.orgId === activeHighlightId}
                tokens={tokens}
                onSelect={() => handleSelect(m.orgId)}
              />
            ))}
            {canCreateOrg && (
              <Box
                as="button"
                w="100%"
                px={4}
                py={3}
                background="transparent"
                border={`1px dashed ${tokens.cardHoverBorder}`}
                borderRadius="16px"
                cursor="pointer"
                textAlign="left"
                onClick={() => {
                  onClose()
                  navigate(routes.createOrganization.value)
                }}
                _hover={{ borderColor: tokens.cardBorderActive }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              >
                <HStack spacing={3}>
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    border={`1px solid ${tokens.cardHoverBorder}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color={tokens.text}
                    fontSize="20px"
                    fontWeight={500}
                  >
                    +
                  </Box>
                  <Text color={tokens.text} fontSize="15px" fontWeight={600} m={0}>
                    Create new organization
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default observer(SwitchTeamModal)
